"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { FIELD, LINK, SEGMENT_OFF, SEGMENT_ON } from "@/lib/ui-classes";
import { Calculator, Info, MapPin, ExternalLink, ChevronDown } from "lucide-react";
import Link from "@/lib/nav-link";
import { formatPrice, trackCalcEvent } from "@/lib/api";
import { noteEntry } from "@/lib/entry-context";
import { useT, useLocale } from "@/lib/locale-context";
import LocationPicker, { type SelectedLocation } from "@/components/LocationPicker";
import { Field } from "@/components/calc/Field";
import {
  TAX_ZONES,
  findTaxZone,
  computeSaleTax,
  computeHouseSaleTax,
  computeLandSaleTax,
  coefColor,
  RESIDENTIAL_EXEMPT_M2,
  taxZoneBlogSlug,
} from "@/lib/taxZones";

type PropertyType = "apartment" | "house" | "land";

// Sane area ceiling - no negative or billion-m² apartments.
const MAX_AREA = 10000; // m²

/** Click-to-open info popover. Mobile-safe (no hover) and can hold a
 *  link, unlike a native `title=` tooltip. Links out to the tax-zones
 *  blog post for the full explanation. */
function InfoTip({ text, blogHref }: { text: string; blogHref: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span className="relative inline-flex align-middle">
      <button
        type="button"
        aria-label={t("tax.calc.tip.aria")}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        className="p-0.5 rounded-full text-gray-400 hover:text-gray-700 transition-colors"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <span
            id={id}
            role="tooltip"
            className="absolute left-1/2 top-full z-20 mt-1.5 w-60 -translate-x-1/2 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-xl"
          >
            <span className="block text-xs leading-relaxed text-gray-600">{text}</span>
            <Link
              href={blogHref}
              onClick={() => setOpen(false)}
              className={`mt-2 inline-flex items-center gap-1 text-[11px] ${LINK}`}
            >
              <ExternalLink className="w-3 h-3 shrink-0" />
              {t("tax.calc.tip.more")}
            </Link>
          </span>
        </>
      )}
    </span>
  );
}

/** Zone picker - a custom dropdown so the value + options can carry the
 *  zone's ramp-color dot, which a native <select> can't render. */
function ZoneSelect({
  value,
  onChange,
  t,
}: {
  value: string | null;
  onChange: (zlabel: string) => void;
  t: (k: "tax.calc.selectzone" | "map.zones.zone") => string;
}) {
  const [open, setOpen] = useState(false);
  const selected = TAX_ZONES.find((z) => z.zlabel === value) ?? null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left outline-none ${FIELD}`}
      >
        {selected ? (
          <>
            <span
              className="h-2.5 w-2.5 rounded-full shrink-0"
              style={{ backgroundColor: coefColor(selected.coef) }}
            />
            <span className="truncate text-base font-extrabold tabular-nums text-gray-900">{selected.zlabel}</span>
            <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-500">×{selected.coef}</span>
          </>
        ) : (
          <span className="truncate text-[13px] text-gray-400">{t("tax.calc.selectzone")}</span>
        )}
        <ChevronDown className="ml-auto w-4 h-4 text-gray-400 shrink-0" />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl">
            {TAX_ZONES.map((z) => (
              <button
                key={z.zlabel}
                type="button"
                onClick={() => {
                  onChange(z.zlabel);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 ${
                  z.zlabel === value ? "bg-gray-50" : ""
                }`}
              >
                <span
                  className="h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: coefColor(z.coef) }}
                />
                <span className="text-sm text-gray-500">{t("map.zones.zone")}</span>
                <span className="text-sm font-extrabold tabular-nums text-gray-900">{z.zlabel}</span>
                <span className="ml-auto text-sm font-semibold text-gray-500 tabular-nums">
                  ×{z.coef}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Estimated simplified sale-tax. Updates live (no submit).
 *
 *  - `compact`: a small read-only readout for the price-result rail
 *    (auto-detected zone, the number, a 3-year toggle, a link to the
 *    full tab). No inputs.
 *  - `standalone`: the full /calculate tab - address (LocationPicker →
 *    auto-zone) or manual zone, area, two-column layout, usage logged. */
export default function TaxZoneCalculator({
  area_m2,
  latitude,
  longitude,
  standalone = false,
  trackUsage = false,
  compact = false,
}: {
  area_m2?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  standalone?: boolean;
  trackUsage?: boolean;
  compact?: boolean;
}) {
  const t = useT();
  const { locale } = useLocale();
  const blogHref = `/${locale}/blog/${taxZoneBlogSlug(locale)}`;

  const [propertyType, setPropertyType] = useState<PropertyType>("apartment");
  const [areaStr, setAreaStr] = useState(String(Math.round(area_m2 ?? 80)));
  const [landAreaStr, setLandAreaStr] = useState("400");
  const [zlabel, setZlabel] = useState<string | null>(null);
  const [autoDetected, setAutoDetected] = useState(false);
  const [registered3Years, setRegistered3Years] = useState(false);
  const [loc, setLoc] = useState<SelectedLocation | null>(null);
  // Whether the user has manually picked a zone - if so, a late-arriving
  // address auto-detection must not clobber their choice.
  const userPicked = useRef(false);

  // Resolve the zone from property coordinates (embedded + compact).
  useEffect(() => {
    let cancelled = false;
    if (latitude == null || longitude == null) return;
    findTaxZone(latitude, longitude).then((zone) => {
      if (cancelled || !zone || userPicked.current) return;
      setZlabel(zone.zlabel);
      setAutoDetected(true);
    });
    return () => {
      cancelled = true;
    };
  }, [latitude, longitude]);

  // Standalone: re-detect the zone whenever the address changes.
  const onLocationChange = (next: SelectedLocation | null) => {
    setLoc(next);
    userPicked.current = false;
    if (!next) return;
    findTaxZone(next.latitude, next.longitude).then((zone) => {
      if (zone && !userPicked.current) {
        setZlabel(zone.zlabel);
        setAutoDetected(true);
      }
    });
    // The hub may ask "selling in this district?" (lib/entry-context);
    // the point rounded to ~1 km, enough to name the district.
    noteEntry("sale_tax", { place: next.district ?? undefined, lat: Math.round(next.latitude * 100) / 100, lng: Math.round(next.longitude * 100) / 100 });
  };

  const coef = useMemo(
    () => TAX_ZONES.find((z) => z.zlabel === zlabel)?.coef ?? null,
    [zlabel],
  );
  const area = Math.min(MAX_AREA, Math.max(0, Number(areaStr) || 0));
  const landArea = Math.min(MAX_AREA, Math.max(0, Number(landAreaStr) || 0));
  const taxableArea = Math.max(0, area - RESIDENTIAL_EXEMPT_M2);
  // The compact rail is apartment-only; only the standalone tab can switch
  // to the house formula.
  const isHouse = standalone && propertyType === "house";
  const isLand = standalone && propertyType === "land";
  const result =
    coef != null ? computeSaleTax({ areaM2: area, coef, registered3Years }) : null;
  const houseResult =
    coef != null && isHouse
      ? computeHouseSaleTax({
          houseAreaM2: area,
          landAreaM2: landArea,
          coef,
          registered3Years,
        })
      : null;
  // Land-only sale: the main area input is the LAND area here. Just the
  // land component - no 30 m² exemption, no 3-year residence exemption.
  const landResult =
    coef != null && isLand
      ? computeLandSaleTax({ landAreaM2: area, coef })
      : null;
  const hasResult = isHouse
    ? houseResult != null
    : isLand
      ? landResult != null
      : result != null;

  // Log one usage event per session once a real estimate exists (only
  // the standalone tab tracks; the rail copy is part of the `sell` event).
  const tracked = useRef(false);
  useEffect(() => {
    if (trackUsage && hasResult && !tracked.current) {
      tracked.current = true;
      // Owner ask: the log carries the calculation's numbers.
      void trackCalcEvent("tax", {
        kind: isHouse ? "house" : isLand ? "land" : "apartment",
        area: area ?? null,
        coef: coef ?? null,
        tax:
          (isHouse
            ? houseResult?.tax
            : isLand
              ? landResult?.tax
              : result?.tax) ?? null,
      });
    }
  }, [trackUsage, hasResult]);

  // ── Compact rail readout ──────────────────────────────────────────
  if (compact) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-1 text-[11px] font-semibold text-gray-500">
            {t("tax.calc.compact.title")}
            <InfoTip text={t("tax.calc.tip.what")} blogHref={blogHref} />
          </div>
          <Link
            href={blogHref}
            className={`inline-flex items-center gap-1 text-[11px] shrink-0 ${LINK}`}
          >
            <ExternalLink className="w-3 h-3 shrink-0" />
            {t("tax.calc.learnmore")}
          </Link>
        </div>

        {result == null ? (
          <Link
            href={`/${locale}/calculate/sale-tax`}
            className={`mt-2 inline-block text-xs ${LINK}`}
          >
            {t("tax.calc.compact.cta")}
          </Link>
        ) : (
          <>
            <div className="mt-1 text-2xl font-extrabold tabular-nums leading-none">
              {result.exemptByResidence ? (
                <span className="text-xl text-emerald-600">{t("tax.calc.exempt")}</span>
              ) : (
                <span className="text-gray-900">
                  {formatPrice(result.tax)}
                  <span className="ml-1 text-xs font-medium text-gray-400">AZN</span>
                </span>
              )}
            </div>
            <div className="mt-1 text-[11px] text-gray-400 tabular-nums">
              {t("map.zones.zone")} {zlabel} · ×{coef} · {Math.round(taxableArea)} m²
            </div>
            <label className="mt-2.5 flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={registered3Years}
                onChange={(e) => setRegistered3Years(e.target.checked)}
                className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-gray-300"
              />
              <span className="text-[11px] text-gray-600">
                {t("tax.calc.registered.short")}
              </span>
            </label>
          </>
        )}
      </div>
    );
  }

  // ── Full card (standalone tab) ────────────────────────────────────
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-7">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Calculator className="w-4 h-4 text-gray-400 shrink-0" />
          <h3 className="text-base font-bold text-gray-900 truncate">
            {t("tax.calc.title")}
          </h3>
        </div>
        <Link
          href={blogHref}
          className={`inline-flex items-center gap-1 text-[11px] shrink-0 ${LINK}`}
        >
          <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          {t("tax.calc.learnmore")}
        </Link>
      </div>

      {/* Property-type toggle - apartment keeps the original behaviour;
          house adds a land component on top of the residential formula.
          One segmented row, the picked kind in the brand tint (owner,
          2026-10-31: "a single row", the picked one should stand out). */}
      <div
        role="radiogroup"
        aria-label={t("sell.proptype")}
        className="mt-5 flex rounded-xl bg-gray-100 p-1 sm:max-w-md"
      >
        {(["apartment", "house", "land"] as const).map((pt) => (
          <button
            key={pt}
            type="button"
            role="radio"
            onClick={() => setPropertyType(pt)}
            aria-checked={propertyType === pt}
            className={`h-8 flex-1 rounded-lg px-1 text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
              propertyType === pt ? SEGMENT_ON : SEGMENT_OFF
            }`}
          >
            {t(
              pt === "apartment"
                ? "tax.calc.type.apartment"
                : pt === "house"
                  ? "tax.calc.type.house"
                  : "tax.calc.type.land",
            )}
          </button>
        ))}
      </div>

      {/* Two columns on desktop: inputs left, the result as a vertically
          centered answer panel on the right (so the short result reads as
          intentional next to the taller address-map column). */}
      {/* Reserve the tallest mode's height (House) so switching tabs
          doesn't make the card jump - the shorter Apartment/Land tabs
          just hold the extra space (the result panel centers within it). */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2 lg:gap-8 lg:items-stretch lg:min-h-[17rem]">
        {/* Inputs. Zone can be set two ways - from the address, OR picked
            directly - so they read as alternatives split by an "or" rule. */}
        <div className="space-y-4">
          {standalone && (
            <>
              <LocationPicker compact value={loc} onChange={onLocationChange} />
              <div className="flex items-center gap-3" aria-hidden>
                <span className="h-px flex-1 bg-gray-200" />
                <span className="text-[10px] font-semibold text-gray-400">
                  {t("common.or")}
                </span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>
            </>
          )}

          {/* Zone, area and (for a house) its land on one row: the zone
              picker is a short control, not a section (owner, 2026-09-21). */}
          <div className={`grid gap-x-3 gap-y-3 ${isHouse ? "grid-cols-3" : "grid-cols-2"}`}>
            <div>
              <label className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-gray-500">
                {t("map.zones.zone")}
                {autoDetected && zlabel != null && (
                  <span className="inline-flex items-center gap-0.5 font-medium normal-case text-[10px] text-emerald-600">
                    <MapPin className="h-3 w-3" />
                    {t("tax.calc.zoneauto")}
                  </span>
                )}
              </label>
              <ZoneSelect
                value={zlabel}
                onChange={(z) => {
                  userPicked.current = true;
                  setZlabel(z);
                  setAutoDetected(false);
                }}
                t={t}
              />
            </div>
            <Field
              size="sm"
              label={isHouse ? t("tax.calc.housearea") : isLand ? t("tax.calc.landarea") : t("tax.calc.area")}
              value={areaStr}
              onChange={setAreaStr}
              unit="m²"
              min={0}
              max={MAX_AREA}
              slider={{ min: isLand ? 100 : 20, max: isLand ? 5000 : 500, step: isLand ? 50 : 5 }}
            />
            {isHouse && (
              <Field
                size="sm"
                label={t("tax.calc.landarea")}
                value={landAreaStr}
                onChange={setLandAreaStr}
                unit="m²"
                min={0}
                max={MAX_AREA}
                slider={{ min: 100, max: 5000, step: 50 }}
              />
            )}
          </div>

          {(isHouse || isLand) && (
            <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-gray-400">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              {t("tax.calc.agriland")}
            </p>
          )}

          {/* The 3-year residence exemption is a residence concept; it does
              not apply to a bare land plot, so hide it in land mode. */}
          {!isLand && (
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={registered3Years}
                onChange={(e) => setRegistered3Years(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-gray-300"
              />
              <span className="text-xs text-gray-700 leading-relaxed">
                {t("tax.calc.registered")}
                <span className="ml-1 inline-block">
                  <InfoTip text={t("tax.calc.tip.residence")} blogHref={blogHref} />
                </span>
              </span>
            </label>
          )}
        </div>

        {/* Result - a full-height answer panel beside the inputs. The
            estimated tax is the hero; taxable area + the formula fill it
            out so the column doesn't read empty. */}
        <div className="flex flex-col gap-3 lg:h-full">
          {!hasResult ? (
            <div className="flex flex-1 items-center justify-center rounded-xl bg-gray-50 p-4 sm:p-6 text-center text-sm text-gray-400">
              {t("tax.calc.pickzonehint")}
            </div>
          ) : isHouse && houseResult != null ? (
            <div className="flex flex-1 flex-col justify-center gap-5 rounded-xl bg-gray-50 p-4 sm:p-7">
              {/* The total tax - the hero. House + land are summed; land is
                  taxed even when the residence exemption zeroes the house. */}
              <div>
                <div className="text-[11px] font-semibold text-gray-500">
                  {t("tax.calc.result")}
                </div>
                <div className="mt-1.5 font-extrabold tabular-nums leading-none">
                  <span className="text-4xl sm:text-5xl text-gray-900">
                    {formatPrice(houseResult.tax)}
                    <span className="ml-2 text-xl font-semibold text-gray-400">AZN</span>
                  </span>
                </div>
              </div>

              {/* Breakdown - house part + land part. */}
              <div className="space-y-3 border-t border-gray-200 pt-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">{t("tax.calc.breakdown.house")}</span>
                  <span className="ml-auto font-bold text-gray-900 tabular-nums">
                    {houseResult.exemptByResidence ? (
                      <span className="text-emerald-600 font-semibold">
                        {t("tax.calc.exempt")}
                      </span>
                    ) : (
                      <>
                        {formatPrice(houseResult.houseTax)}{" "}
                        <span className="font-normal text-gray-400">AZN</span>
                      </>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">{t("tax.calc.breakdown.land")}</span>
                  <span className="ml-auto font-bold text-gray-900 tabular-nums">
                    {formatPrice(houseResult.landTax)}{" "}
                    <span className="font-normal text-gray-400">AZN</span>
                  </span>
                </div>
              </div>

              {/* Formula chip - same treatment as the apartment tab. */}
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-center text-xs leading-relaxed font-medium text-gray-600">
                {t("tax.calc.formulachip.house")}
              </div>
            </div>
          ) : isLand && landResult != null ? (
            <div className="flex flex-1 flex-col justify-center gap-5 rounded-xl bg-gray-50 p-4 sm:p-7">
              {/* Land-only: the land component is the whole tax. */}
              <div>
                <div className="text-[11px] font-semibold text-gray-500">
                  {t("tax.calc.result")}
                </div>
                <div className="mt-1.5 font-extrabold tabular-nums leading-none">
                  <span className="text-4xl sm:text-5xl text-gray-900">
                    {formatPrice(landResult.tax)}
                    <span className="ml-2 text-xl font-semibold text-gray-400">AZN</span>
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 border-t border-gray-200 pt-4 text-sm">
                <span className="text-gray-500">{t("tax.calc.breakdown.land")}</span>
                <span className="ml-auto font-bold text-gray-900 tabular-nums">
                  {Math.round(area)} m² × 0.5 × {coef}
                </span>
              </div>

              {/* Formula chip - same treatment as the apartment tab. */}
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-center text-sm font-medium text-gray-600">
                {t("tax.calc.formulachip.land")}
              </div>
            </div>
          ) : result != null ? (
            <div className="flex flex-1 flex-col justify-center gap-5 rounded-xl bg-gray-50 p-4 sm:p-7">
              {/* The tax amount - the moment. */}
              <div>
                <div className="text-[11px] font-semibold text-gray-500">
                  {t("tax.calc.result")}
                </div>
                <div className="mt-1.5 font-extrabold tabular-nums leading-none">
                  {result.exemptByResidence ? (
                    <span className="text-2xl text-emerald-600">{t("tax.calc.exempt")}</span>
                  ) : (
                    <span className="text-4xl sm:text-5xl text-gray-900">
                      {formatPrice(result.tax)}
                      <span className="ml-2 text-xl font-semibold text-gray-400">AZN</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Taxable area - the basis. */}
              <div className="flex items-center gap-2 border-t border-gray-200 pt-4 text-sm">
                <span className="flex items-center gap-1 text-gray-500">
                  {t("tax.calc.taxablearea")}
                  <InfoTip text={t("tax.calc.tip.deduction")} blogHref={blogHref} />
                </span>
                <span className="ml-auto font-bold text-gray-900 tabular-nums">
                  {Math.round(taxableArea)} m²{" "}
                  <span className="font-normal text-gray-400">
                    ({Math.round(area)} − {RESIDENTIAL_EXEMPT_M2})
                  </span>
                </span>
              </div>

              {/* Formula - prominent, on white so it reads as a chip. */}
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-center text-sm font-medium text-gray-600">
                {t("tax.calc.formulachip")}
              </div>
            </div>
          ) : null}
          <p className="text-[10px] text-gray-400 leading-relaxed">
            {t("tax.calc.disclaimer")}
          </p>
        </div>
      </div>
    </div>
  );
}
